import { Link } from "wouter";
import { Mail, CalendarDays, Building2, Bell, Plus } from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer";
import { Button } from "../components/ui/button";

const FEATURES = [
  {
    icon: CalendarDays,
    title: "Every event in one place",
    description:
      "Socials, classes, and workshops across New England — browse them as a list, a calendar, or a map.",
  },
  {
    icon: Building2,
    title: "Venues and studios",
    description:
      "Find the rooms you dance in, see what's coming up there, and follow the studios you love.",
  },
  {
    icon: Bell,
    title: "Reminders",
    description:
      "Get a single email about a day before an event you don't want to miss. No account required.",
  },
  {
    icon: Plus,
    title: "Community submitted",
    description:
      "Anyone can add an event. Organizers and studios can claim a profile and manage their own listings.",
  },
];

export function AboutPage() {
  return (
    <PageContainer>
      {/* Header */}
      <header className="mb-10">
        <h1 className="font-display text-3xl text-foreground mb-3">About NE Dances</h1>
        <p className="text-muted-foreground leading-relaxed">
          NE Dances is a free directory of social dance events in New England — Swing, Ballroom,
          Salsa, Bachata, and everything in between. It exists because
          finding out where to dance this week shouldn't mean digging through a dozen Facebook
          groups, flyers, and group chats.
        </p>
      </header>

      {/* What you can do */}
      <section className="mb-10">
        <h2 className="font-display text-xl text-foreground mb-4">What you can do here</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="flex flex-col gap-3 p-5 rounded-xl border border-border/50 bg-card"
              >
                <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center text-primary">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm mb-1">{feature.title}</p>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Who made it */}
      <section className="mb-10">
        <h2 className="font-display text-xl text-foreground mb-3">Who made it</h2>
        <p className="text-muted-foreground leading-relaxed">
          NE Dances was created in 2026 by <span className="text-foreground font-medium">Nate Munic</span>{" "}
          for the New England social dance community. It's built and maintained as a community
          resource — the listings come from dancers, organizers, and studios across the region,
          and the site grows as they add to it.
        </p>
      </section>

      {/* Contact */}
      <section className="mb-10">
        <h2 className="font-display text-xl text-foreground mb-3">Get in touch</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          Questions, corrections, a venue that should be listed, or something that looks broken —
          send it over. Feedback from the community is what shapes this site.
        </p>
        <a
          href="mailto:Info@nedances.com"
          className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
        >
          <Mail className="w-4 h-4" />
          Info@nedances.com
        </a>
      </section>

      {/* CTA */}
      <div className="flex flex-wrap gap-3">
        <Button asChild variant="contrast" size="sm">
          <Link href="/submit">
            <Plus className="w-4 h-4" />
            Submit an Event
          </Link>
        </Button>
        <Button asChild variant="ghost" size="sm">
          <Link href="/events">
            <CalendarDays className="w-4 h-4" />
            Browse Events
          </Link>
        </Button>
      </div>
    </PageContainer>
  );
}
